import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/utils";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn, ShieldCheck, Boxes, ClipboardCheck, Eye, EyeOff } from "lucide-react";

const LOGO_SRC = "/logo.jpg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }} data-testid="login-page">
      {/* Brand panel (hidden on small screens) */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden flex-col justify-between p-12"
        style={{
          background:
            "linear-gradient(135deg, #0d2a4e 0%, #1e5fbf 60%, #2a78d9 100%)",
        }}
      >
        {/* Decorative red accent ribbon */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "-60px",
            right: "-60px",
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(224,50,43,0.55) 0%, rgba(224,50,43,0) 70%)",
            filter: "blur(8px)",
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            bottom: "-80px",
            left: "-80px",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
          }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <img
            src={LOGO_SRC}
            alt="SM Marine Products"
            style={{ width: 56, height: 56, objectFit: "contain", background: "#fff", padding: 4, borderRadius: 10, boxShadow: "var(--shadow-md)" }}
          />
          <div>
            <div className="font-heading font-bold text-lg tracking-tight text-white">SM MARINE PRODUCTS</div>
            <div className="text-xs font-mono" style={{ color: "rgba(255,255,255,0.7)" }}>FISHMEAL WMS · FINISHED GOODS</div>
          </div>
        </div>

        <div className="relative z-10">
          <h1 className="font-heading text-5xl font-bold leading-tight tracking-tight mb-4 text-white">
            Warehouse<br />Operations,<br />
            <span style={{ color: "#ffb3b0" }}>Engineered.</span>
          </h1>
          <p className="text-sm max-w-md" style={{ color: "rgba(255,255,255,0.78)" }}>
            Batch-wise inward, lab QC, blending averages, multi-warehouse transfers and FIFO dispatch — all in one secure control panel.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-3 max-w-md">
            {[
              { icon: Boxes, label: "Batch-wise inventory across 3 warehouses" },
              { icon: ClipboardCheck, label: "In-house + Accurate Lab QC reports" },
              { icon: ShieldCheck, label: "Role-based audit trail for every action" },
            ].map((it) => (
              <div
                key={it.label}
                className="flex items-center gap-3 px-4 py-2.5"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 10,
                  backdropFilter: "blur(8px)",
                }}
              >
                <it.icon size={16} color="#ffffff" />
                <span className="text-sm text-white">{it.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 font-mono text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
          SM MARINE PRODUCTS · v1.0 · 50 KG BAG STANDARD
        </div>
      </div>

      {/* Sign-in panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          {/* Logo only on small/medium screens (where brand panel is hidden) */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img
              src={LOGO_SRC}
              alt="SM Marine Products"
              style={{ width: 52, height: 52, objectFit: "contain", background: "#fff", padding: 3, borderRadius: 10, boxShadow: "var(--shadow-sm)" }}
            />
            <div>
              <div className="font-heading font-bold tracking-tight" style={{ color: "var(--text)" }}>SM MARINE PRODUCTS</div>
              <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>FISHMEAL WMS</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>// SECURE ACCESS</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Sign in</h2>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Enter your credentials to access the control panel.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@smmarine.co"
                required
                autoFocus
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: 40 }}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((s) => !s)}
                  data-testid="toggle-password-visibility"
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--text-muted)",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {err && (
              <div
                className="text-xs p-2.5"
                style={{
                  color: "var(--brand-red)",
                  border: "1px solid var(--brand-red)",
                  background: "var(--brand-red-soft)",
                  borderRadius: 8,
                }}
                data-testid="login-error"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              <LogIn size={16} /> {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-10 text-xs font-mono text-center" style={{ color: "var(--text-dim)" }}>
            © SM Marine Products · Secure WMS
          </div>
        </div>
      </div>
    </div>
  );
}
